import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { ArrowLeft, ArrowUpRight, BedDouble, Bath, Ruler, Car, LandPlot, MapPin, Check } from "lucide-react";
import { API, STATUS_COLORS, formatPrice, resolveImage, communityFor } from "../data/site";
import { FadeUp } from "../components/motion";
import SEO from "../components/SEO";

const HomeDetail = () => {
  const { id } = useParams();
  const [activeImg, setActiveImg] = useState(0);
  const { data: home, isLoading, isError } = useQuery({
    queryKey: ["home", id],
    queryFn: async () => (await axios.get(`${API}/homes/${id}`)).data,
  });

  if (isLoading)
    return <main className="pt-40 pb-32 max-w-[1600px] mx-auto px-5 md:px-10"><p className="font-mono text-xs tracking-[0.2em] uppercase text-[#5C5A56]" data-testid="home-loading">Loading…</p></main>;
  if (isError || !home)
    return (
      <main className="pt-40 pb-32 max-w-[1600px] mx-auto px-5 md:px-10" data-testid="home-not-found">
        <h1 className="font-serif text-5xl">Home not found</h1>
        <Link to="/available-homes" className="mt-6 inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase font-bold link-underline"><ArrowLeft size={14} /> Back to Available Homes</Link>
      </main>
    );

  const images = (home.images || []).map(resolveImage);
  const community = communityFor(home);
  const tourUrl = `/contact?interest=${encodeURIComponent("Buying an Available Home")}&home=${encodeURIComponent(home.title)}`;
  const specs = [
    { icon: BedDouble, label: "Bedrooms", value: home.beds },
    { icon: Bath, label: "Bathrooms", value: home.baths },
    { icon: Ruler, label: "Square Feet", value: home.sqft?.toLocaleString() },
    { icon: Car, label: "Garage", value: home.garage },
    { icon: LandPlot, label: "Lot", value: home.lot },
    { icon: MapPin, label: "Location", value: home.city },
  ].filter((s) => s.value !== null && s.value !== undefined && s.value !== "");

  return (
    <main>
      <SEO title={`${home.title} | ${home.city || "Idaho"} New Construction Home | On Point Homes`} description={home.description?.slice(0, 155)} />
      <div className="pt-24 md:pt-32 pb-24 md:pb-36 max-w-[1600px] mx-auto px-5 md:px-10">
        <FadeUp>
          <Link to="/available-homes" data-testid="back-to-homes" className="inline-flex items-center gap-2 font-mono text-[0.65rem] tracking-[0.2em] uppercase font-bold text-[#5C5A56] hover:text-[#1A1A1A] transition-colors duration-300">
            <ArrowLeft size={14} /> Available Homes
          </Link>
          <div className="mt-8 flex flex-wrap items-end justify-between gap-6 pb-10 border-b border-[rgba(26,26,26,0.12)]">
            <div>
              <span className={`inline-block font-mono text-[0.6rem] tracking-[0.2em] uppercase font-bold px-3 py-1.5 mb-4 ${STATUS_COLORS[home.status]}`} data-testid="home-status">
                {home.status}
              </span>
              <h1 className="font-serif text-5xl md:text-7xl tracking-tight leading-[0.98]">{home.title}</h1>
              <p className="mt-3 text-[#5C5A56]">{home.address !== "Coming Soon" && `${home.address}, `}{home.city}</p>
            </div>
            {home.price && <p className="font-mono text-2xl md:text-3xl font-bold" data-testid="home-price">{formatPrice(home.price)}</p>}
          </div>
        </FadeUp>

        {images.length > 0 && (
          <FadeUp delay={0.1} className="mt-10">
            <div className="img-zoom aspect-[16/9] bg-[#EFECE7]">
              <img src={images[activeImg]} alt={`${home.title} photo ${activeImg + 1} — On Point Homes Idaho`} className="w-full h-full object-cover" data-testid="home-main-image" />
            </div>
            {images.length > 1 && (
              <div className="mt-3 grid grid-cols-4 md:grid-cols-6 gap-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    data-testid={`home-thumb-${i}`}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-[4/3] overflow-hidden ${i === activeImg ? "ring-2 ring-[#8C6A4F]" : "opacity-60 hover:opacity-100"} transition-opacity duration-300`}
                  >
                    <img src={img} alt={`${home.title} thumbnail ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </FadeUp>
        )}

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <FadeUp>
              <p className="eyebrow text-[#8C6A4F] mb-6">About This Home</p>
              <p className="text-base md:text-lg leading-relaxed text-[#5C5A56] whitespace-pre-line" data-testid="home-description">{home.description}</p>
            </FadeUp>
            {home.features?.length > 0 && (
              <FadeUp delay={0.1} className="mt-12">
                <p className="eyebrow text-[#8C6A4F] mb-6">Features</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3" data-testid="home-features">
                  {home.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-[#5C5A56] py-2 border-b border-[rgba(26,26,26,0.08)]">
                      <Check size={15} className="text-[#8C6A4F] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </FadeUp>
            )}
            {home.floor_plans?.length > 0 && (
              <FadeUp delay={0.15} className="mt-12">
                <p className="eyebrow text-[#8C6A4F] mb-6">Floor Plans</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="home-floorplans">
                  {home.floor_plans.map((fp, i) => (
                    <a key={i} href={resolveImage(fp)} target="_blank" rel="noreferrer" className="img-zoom aspect-[4/3] block bg-[#EFECE7]" data-testid={`floorplan-${i}`}>
                      <img src={resolveImage(fp)} alt={`${home.title} floor plan ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                    </a>
                  ))}
                </div>
              </FadeUp>
            )}
            {home.city && (
              <FadeUp delay={0.2} className="mt-12">
                <p className="eyebrow text-[#8C6A4F] mb-6">Location</p>
                <div className="aspect-[16/8] bg-[#EFECE7] overflow-hidden" data-testid="home-map">
                  <iframe
                    title={`Map of ${home.title} location`}
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(`${home.address !== "Coming Soon" ? home.address : ""} ${home.city}`)}&output=embed`}
                    className="w-full h-full border-0 grayscale"
                    loading="lazy"
                  />
                </div>
              </FadeUp>
            )}
          </div>

          <div className="lg:col-span-5">
            <FadeUp delay={0.1} className="lg:sticky lg:top-28">
              <div className="border border-[rgba(26,26,26,0.15)] p-8">                <p className="eyebrow text-[#8C6A4F] mb-6">The Details</p>
                <dl className="divide-y divide-[rgba(26,26,26,0.08)]">
                  {specs.map((s, i) => (
                    <div key={i} className="flex items-center justify-between py-4">
                      <dt className="flex items-center gap-3 text-sm text-[#5C5A56]"><s.icon size={16} className="text-[#8C6A4F]" /> {s.label}</dt>
                      <dd className="font-mono text-sm font-semibold">{s.value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-8 flex flex-col gap-3">
                  <Link to={tourUrl} data-testid="schedule-tour-btn" className="btn-sweep bg-[#1A1A1A] text-[#F7F5F2] font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-8 py-4 text-center">
                    Schedule a Tour
                  </Link>
                  <Link to={tourUrl} data-testid="request-info-btn" className="border border-[#1A1A1A]/30 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-8 py-4 text-center hover:bg-[#1A1A1A] hover:text-[#F7F5F2] transition-colors duration-500">
                    Request More Information
                  </Link>
                  {home.listing_url && (
                    <a
                      href={home.listing_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="view-listing-link"
                      className="inline-flex items-center justify-center gap-2 font-mono text-[0.65rem] tracking-[0.18em] uppercase font-bold text-[#5C5A56] hover:text-[#8C6A4F] transition-colors duration-300 pt-2 link-underline w-fit mx-auto"
                    >
                      View on {(() => { try { return new URL(home.listing_url).hostname.replace("www.", "").split(".")[0]; } catch { return "Listing Site"; } })()} <ArrowUpRight size={13} />
                    </a>
                  )}
                </div>
              </div>
              {community && (
                <div className="border border-[rgba(26,26,26,0.15)] p-8 mt-6" data-testid="community-card">
                  <p className="eyebrow text-[#8C6A4F] mb-4">The Community</p>
                  <p className="font-serif text-2xl tracking-tight mb-5">{community.name}</p>
                  <div className="flex flex-col gap-3">
                    {community.links.map((l) => (
                      <a
                        key={l.url}
                        href={l.url}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`community-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                        className="link-underline w-fit inline-flex items-center gap-2 text-sm font-medium text-[#5C5A56] hover:text-[#8C6A4F] transition-colors duration-300"
                      >
                        {l.label} <ArrowUpRight size={13} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </FadeUp>
          </div>
        </div>
      </div>
    </main>
  );
};

export default HomeDetail;
